/**
 * VAT helpers — VAT-INCLUSIVE pricing model.
 *
 * Owner directive (locked 2026-05-11):
 *   Every priceCents in the product master is the customer-paid
 *   amount, VAT already included. We never add VAT on top at the
 *   till — instead we compute the VAT portion FROM the inclusive
 *   total.
 *
 * For a 15% VAT rate:
 *   totalIncl   = sum(unitPriceCents * qty)  ← what customer pays
 *   vatPortion  = round(totalIncl * 15 / 115)
 *   subtotal    = totalIncl - vatPortion     ← ex-VAT amount
 *
 * Rounding is bankers'-rounded to the nearest cent. We always do
 * the math in integer cents — never in floats.
 *
 * Cohesion rule 1: ONE source of truth for VAT math. Every cart,
 * receipt, KDS ticket, report, and statement uses this helper.
 * Do not inline `* 0.15` or `* 15 / 115` anywhere else.
 */

export const VAT_RATE_BPS = 1500; // basis points — 15.00%

/**
 * Given a VAT-inclusive total, returns the VAT portion in cents.
 */
export function vatPortionOfInclusive(totalInclCents: number, rateBps = VAT_RATE_BPS): number {
  // VAT-incl / (1 + rate) = ex-VAT.  VAT portion = incl - ex.
  // To avoid float drift, do: incl * rate / (10000 + rate)  with rounding.
  return Math.round((totalInclCents * rateBps) / (10000 + rateBps));
}

/**
 * Given a VAT-inclusive total, returns the ex-VAT subtotal.
 */
export function exVatOfInclusive(totalInclCents: number, rateBps = VAT_RATE_BPS): number {
  return totalInclCents - vatPortionOfInclusive(totalInclCents, rateBps);
}

/**
 * Convenience — returns the three numbers in one go.
 */
export interface VatBreakdown {
  /** Pre-VAT amount (cents). */
  subtotalCents: number;
  /** VAT portion (cents). */
  vatCents: number;
  /** VAT-inclusive total — what the customer pays (cents). */
  totalCents: number;
  /** Effective rate in basis points (for the receipt to say "15%"). */
  rateBps: number;
}

export function breakdownFromInclusive(totalInclCents: number, rateBps = VAT_RATE_BPS): VatBreakdown {
  const vat = vatPortionOfInclusive(totalInclCents, rateBps);
  return {
    subtotalCents: totalInclCents - vat,
    vatCents: vat,
    totalCents: totalInclCents,
    rateBps,
  };
}
